import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Shield as ShieldIcon } from '@mui/icons-material';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import { Button } from '../../Components/MUIs';
import Global from '../../Services/Common/Global';
import Commons from '../../Services/Common/Common';
import { MOK_LOGIN_RESULT_KEY, MOK_IDENTITY_USER_KEY } from './IdentityMokCallback';
import { Helmet } from 'react-helmet';

function IdentityConfirm() {
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const mokDeepLinkListenerRef = useRef(null);

  // 컴포넌트 언마운트 시 미완료 딥링크 리스너 정리
  useEffect(() => {
    return () => {
      if (mokDeepLinkListenerRef.current) {
        mokDeepLinkListenerRef.current.remove();
        mokDeepLinkListenerRef.current = null;
      }
    };
  }, []);

  // PWA redirect 콜백 결과(sessionStorage)를 읽어 SaveLogic1 호출
  useEffect(() => {
    const stored = sessionStorage.getItem(MOK_LOGIN_RESULT_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      // redirect 전에 저장해 둔 userId 복원 (PWA 흐름에서 state 유실 방지)
      const userId = sessionStorage.getItem(MOK_IDENTITY_USER_KEY);
      SaveLogic1(parsed, userId);
    } catch (_) {
      // 무효한 값이면 제거
    } finally {
      sessionStorage.removeItem(MOK_LOGIN_RESULT_KEY);
      sessionStorage.removeItem(MOK_IDENTITY_USER_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MOK 본인인증 결과를 서버에 저장
  const SaveLogic1 = async (mokPayload, userId) => {
    try {
      const result = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Account.updateIdentity,
        {
          ci: Commons.EncryptAES(mokPayload.ci || ''),
          di: Commons.EncryptAES(mokPayload.di || ''),
          issueDate: Commons.EncryptAES(mokPayload.issueDate || ''),
          issuer: Commons.EncryptAES(mokPayload.issuer || ''),
          providerId: Commons.EncryptAES(mokPayload.providerId || ''),
          reqAuthType: Commons.EncryptAES(mokPayload.reqAuthType || ''),
          reqDate: Commons.EncryptAES(mokPayload.reqDate || ''),
          serviceType: Commons.EncryptAES(mokPayload.serviceType || ''),
          siteId: Commons.EncryptAES(mokPayload.siteId || ''),
          txId: Commons.EncryptAES(mokPayload.txId || ''),
          userBirthday: Commons.EncryptAES(mokPayload.userBirthday || ''),
          userGender: Commons.EncryptAES(mokPayload.userGender || ''),
          userName: Commons.EncryptAES(mokPayload.userName || ''),
          foreignerYn: Commons.EncryptAES(mokPayload.userNation || ''),
          userPhone: Commons.EncryptAES(mokPayload.userPhone || ''),
          userId: Commons.EncryptAES(userId || ''),
        }
      );
      let data = result.data;

      if (data.type === 'Error') {
        setErrMsg(data.message);
      }
      else {
        setConfirmed(true);
      }
    } catch (e) {
      setErrMsg(
        e?.response?.data?.message ||
        e?.message ||
        '본인인증 정보 저장에 실패했습니다. 다시 시도해 주세요.'
      );
    }
  };

  // 본인인증 시작 버튼 클릭 핸들러
  const HandleMokAuthClick = async () => {
    setLoading(true);
    setErrMsg('');

    try {
      if (Capacitor.isNativePlatform()) {
        const nativeReturnUrl = `${process.env.REACT_APP_MOK_RETURN_URL}/mok-callback?type=login&from=native`;

        if (mokDeepLinkListenerRef.current) {
          mokDeepLinkListenerRef.current.remove();
          mokDeepLinkListenerRef.current = null;
        }

        const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}?returnUrl=${encodeURIComponent(nativeReturnUrl)}`;
        const httpRes = await CapacitorHttp.get({ url: mokRequestUrl });
        const mokResData = httpRes.data;

        let nativeMokUrl = null;

        if (
          mokResData &&
          typeof mokResData === 'object' &&
          mokResData.encryptReqClientInfo
        ) {
          const sdkOrigin = new URL(process.env.REACT_APP_MOBILE_OK).origin;
          const mokBaseUrl = `${sdkOrigin}/ptb_mokauth.html`;
          const frontendHostname = new URL(process.env.REACT_APP_MOK_RETURN_URL).hostname;
          const params = new URLSearchParams({
            usageCode: mokResData.usageCode || '01001',
            serviceId: mokResData.serviceId,
            convertInfo: 'undefined',
            returnUrl: mokResData.returnUrl,
            encryptVersion: 'undefined',
            encryptReqClientInfo: mokResData.encryptReqClientInfo,
            serviceType: mokResData.serviceType || 'telcoAuth',
            retTransferType: mokResData.retTransferType || 'MOKToken',
            callbackFunction: '',
            alternative: frontendHostname,
          });
          nativeMokUrl = `${mokBaseUrl}?${params.toString()}`;
        }

        if (!nativeMokUrl && typeof mokResData === 'string') {
          const doc = new DOMParser().parseFromString(mokResData, 'text/html');
          const form = doc.querySelector('form');
          if (form) {
            const formAction = form.getAttribute('action');
            const formParams = {};
            form.querySelectorAll('input').forEach((el) => {
              if (el.name) formParams[el.name] = el.value;
            });
            if (formAction && formParams.encryptReqClientInfo) {
              nativeMokUrl = `${formAction}?${new URLSearchParams(formParams).toString()}`;
            }
          }
        }

        if (!nativeMokUrl) {
          throw new Error('본인확인 URL 추출에 실패했습니다. 다시 시도해 주세요.');
        }

        const handle = await CapacitorApp.addListener('appUrlOpen', async (event) => {
          try {
            await Browser.close();
            if (!event.url.includes('mok-callback')) return;

            const url = new URL(event.url);
            const dataParam = url.searchParams.get('data');

            if (dataParam) {
              const parsed = JSON.parse(dataParam);
              if (parsed?.resultCode && String(parsed.resultCode) !== '2000') {
                throw new Error(parsed?.resultMsg || '본인확인에 실패했습니다.');
              }
              const userId = sessionStorage.getItem(MOK_IDENTITY_USER_KEY);
              await SaveLogic1(parsed, userId);
            } else {
              throw new Error('본인확인 결과 정보가 없습니다.');
            }
          } catch (e) {
            setErrMsg(
              e?.response?.data?.message ||
              e?.message ||
              '본인인증 처리에 실패했습니다. 다시 시도해 주세요.'
            );
          } finally {
            if (mokDeepLinkListenerRef.current) {
              mokDeepLinkListenerRef.current.remove();
              mokDeepLinkListenerRef.current = null;
            }
            setLoading(false);
          }
        });

        mokDeepLinkListenerRef.current = handle;
        await Browser.open({ url: nativeMokUrl });
        return;
      } else {
        // PWA 환경
        const returnUrl = `${window.location.origin}/mok-callback?type=login`;
        const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;

        if (window.MOBILEOK && typeof window.MOBILEOK.process === 'function') {
          window.MOBILEOK.process(mokRequestUrl, 'MB', '');
          setLoading(false);
          return;
        }

        throw new Error('본인확인 SDK가 로드되지 않았습니다.');
      }
    } catch (e) {
      setLoading(false);
      setErrMsg(
        e?.response?.data?.message ||
        e?.message ||
        '본인인증 요청에 실패했습니다. 다시 시도해 주세요.'
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 4,
        minHeight: 320,
        gap: 2,
      }}
    >
      <Helmet>
        <script src={process.env.REACT_APP_MOBILE_OK}></script>
      </Helmet>
      {!confirmed ? (
        <>
          {/* 아이콘 + 안내 문구 */}
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShieldIcon sx={{ fontSize: 44, color: '#1F2937' }} />
            </Box>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
              본인인증이 필요합니다
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#6B7280',
                textAlign: 'center',
                lineHeight: 1.7,
              }}
            >
              회원가입 시 본인인증을 완료하지 않았습니다.
              <br />
              안전한 서비스 이용을 위해 본인인증을 진행해 주세요.
            </Typography>
          </Stack>

          {/* 인증 시작 버튼 */}
          <Button
            sx={{
              width: '100%',
              backgroundColor: '#1F2937',
              color: '#FFFFFF',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              height: '52px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#374151' },
              '&.Mui-disabled': { backgroundColor: '#9CA3AF', color: '#FFFFFF' },
            }}
            name={loading ? '요청 중...' : '본인인증 시작'}
            disabled={loading}
            onClick={HandleMokAuthClick}
          />

          {/* 에러 메시지 */}
          {errMsg && (
            <Typography
              sx={{ fontSize: '13px', color: '#DC2626', textAlign: 'center', mt: 0.5 }}
            >
              {errMsg}
            </Typography>
          )}
        </>
      ) : (
        <>
          {/* 인증 완료 상태 */}
          <Stack alignItems="center" spacing={1.5}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#2CA900' }} />
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
              본인인증 완료
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#6B7280',
                textAlign: 'center',
                lineHeight: 1.7,
              }}
            >
              본인인증이 완료되었습니다.
              <br />
              다시 로그인하여 서비스를 이용해 주세요.
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}

export default IdentityConfirm;
